import React from 'react';
import { Check, X, Loader2, MessageCircle } from 'lucide-react';
import {
  PRICING_WHATSAPP_NUMBER,
  PRICING_BILLING_TABS,
  PRICING_GST_PERCENT,
  buildPricingWhatsAppMessage,
  resolvePlanPeriodLabel,
  resolvePlanPrice,
  resolvePlanOriginalPrice,
  resolvePlanTotalWithGstInr,
  formatInr,
  formatPlanFeature,
  getPlanFeatureStruckText,
  isPlanFeatureStruckOnly,
} from '../../../config/pricingPlans';
import { AtelierNav, AtelierFooter } from './AtelierChrome';
import './atelier.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const openWhatsApp = (plan) => {
  const message = buildPricingWhatsAppMessage(plan);
  window.open(`https://wa.me/${PRICING_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
};

const PlanCard = ({ plan, payingPlanId, onPurchase }) => {
  const price = resolvePlanPrice(plan);
  const originalPrice = resolvePlanOriginalPrice(plan);
  const periodLabel = resolvePlanPeriodLabel(plan);
  const busy = payingPlanId !== null;
  const isPayingThis = payingPlanId === plan.id;

  return (
    <article className={`atl-plan${plan.popular ? ' atl-plan--popular' : ''}${plan.image ? ' atl-plan--media' : ''}`}>
      {plan.popular && <div className="atl-plan-badge">Most popular</div>}
      {plan.image && (
        <div className="atl-plan-media">
          <img src={plan.image} alt={`${plan.name} setup`} loading="lazy" />
        </div>
      )}
      <div className="atl-plan-head">
        <h3>{plan.name}</h3>
        <div className="atl-plan-price">
          {originalPrice ? <s>{inr(originalPrice)}</s> : null}
          <span className="atl-plan-amount">{inr(price)}</span>
          <span className="atl-plan-period">{periodLabel}</span>
        </div>
        <div className="atl-plan-gst">
          + {PRICING_GST_PERCENT}% GST · Total ₹{formatInr(resolvePlanTotalWithGstInr(price))} incl. GST
        </div>
      </div>

      <ul className="atl-plan-features">
        {plan.features.map((feature, i) => {
          const struckText = getPlanFeatureStruckText(feature);
          const mainText = formatPlanFeature(feature);
          if (isPlanFeatureStruckOnly(feature)) {
            return <li key={i} className="is-struck"><span className="atl-plan-icon" /><s>{mainText}</s></li>;
          }
          return (
            <li key={i}>
              <Check className="atl-plan-icon" size={18} aria-hidden="true" />
              <span>
                {struckText ? <><s>{struckText}</s>{' '}</> : null}
                {mainText}
              </span>
            </li>
          );
        })}
        {(plan.exclusions || []).map((exclusion, i) => (
          <li key={`ex-${i}`} className="is-excluded">
            <X className="atl-plan-icon" size={18} aria-hidden="true" />
            <span>{exclusion}</span>
          </li>
        ))}
      </ul>

      <div className="atl-plan-actions">
        <button
          type="button"
          className={`atl-link atl-btn atl-plan-buy${plan.popular ? '' : ' atl-btn--ghost'}`}
          disabled={busy}
          onClick={() => onPurchase(plan)}
        >
          {isPayingThis ? (<><Loader2 size={18} className="atl-spin" aria-hidden="true" /> Processing…</>) : 'Purchase'}
        </button>
        <button type="button" className="atl-link atl-plan-wa" onClick={() => openWhatsApp(plan)}>
          <MessageCircle size={16} aria-hidden="true" />
          Questions? Chat on WhatsApp
        </button>
      </div>
    </article>
  );
};

const AtelierPricing = ({
  plans,
  billingTab,
  onBillingTabChange,
  payingPlanId,
  onPurchase,
  navLinks,
  onLogo,
  onSignIn,
  onStartDesigning,
  onBookDemo,
  onAbout,
  onHelp,
  onPrivacy,
}) => (
  <div className="atl-root">
    <AtelierNav links={navLinks} onLogo={onLogo} onSignIn={onSignIn} onStartDesigning={onStartDesigning} />

    <header className="atl-about-hero atl-wrap">
      <div className="atl-pill"><span className="atl-dot" />Plans</div>
      <h1>Simple pricing.</h1>
      <p className="atl-about-lead">Choose the plan that works for you.</p>

      <div className="atl-billing" role="tablist" aria-label="Billing cycle">
        {PRICING_BILLING_TABS.map((tab) => {
          const active = billingTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`atl-billing-tab${active ? ' is-on' : ''}`}
              onClick={() => onBillingTabChange(tab.id)}
            >
              {tab.label}
              {tab.badge ? <span className="atl-billing-badge">{tab.badge}</span> : null}
            </button>
          );
        })}
      </div>
    </header>

    <section className="atl-pricing atl-wrap">
      <div className="atl-plans">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} payingPlanId={payingPlanId} onPurchase={onPurchase} />
        ))}
      </div>
    </section>

    <section className="atl-final atl-wrap">
      <h2>Not sure which plan fits?</h2>
      <p className="atl-final-sub">Book a quick demo and we'll help you pick the right plan.</p>
      <div className="atl-final-ctas">
        <button type="button" className="atl-link atl-btn atl-btn--xl" onClick={onBookDemo}>
          Book a demo
        </button>
        <button type="button" className="atl-link atl-btn atl-btn--xl atl-btn--ghost" onClick={onStartDesigning}>
          Start designing free
        </button>
      </div>
    </section>

    <AtelierFooter onAbout={onAbout} onPricing={() => window.scrollTo({ top: 0, behavior: 'smooth' })} onHelp={onHelp} onPrivacy={onPrivacy} />
  </div>
);

export default AtelierPricing;
